import { Card, CardContent } from '@/components/ui/card';
import { parseTradeDeadline, tradeDeadlineRule } from '@/lib/nflTradeDeadline';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';
import MarketingLayout from '@/components/shared/MarketingLayout';

const sections = [
  { id: 'eligibility', title: '1. Eligibility' },
  { id: 'entry-fees', title: '2. Entry Fees & Prizes' },
  { id: 'scoring', title: '3. Scoring System' },
  { id: 'sabotage-cards', title: '4. Sabotage Cards' },
  { id: 'disputes', title: '5. Disputes & Stat Corrections' },
  { id: 'trades-waivers', title: '6. Trades & Waivers' },
  { id: 'roster-locks', title: '7. Roster Locks' },
  { id: 'tiebreakers', title: '8. Tiebreakers' },
  { id: 'responsible-gaming', title: '9. Responsible Gaming' },
  { id: 'governing-law', title: '10. Governing Law' },
];

function TimingCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 mt-3">
      <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <p className="text-sm text-foreground font-medium">{children}</p>
    </div>
  );
}

export default function ContestRules() {
  // SAB-1058 / SAB-1037: this page said "4:00 AM ET" in two places. The waiver cron is
  // `0 6 * * *` UTC, which is 2:00 AM EDT and 1:00 AM EST -- so the PUBLISHED CONTEST RULES
  // of a real-money product stated a processing time two hours off, and one that does not
  // exist in either offset. Derived from WAIVER_CRON_UTC now, the same constant the
  // settings card and the digest render, so it follows the cron rather than a memory of it.

  return (
    <MarketingLayout>
      <h1 className="text-2xl font-display font-bold text-foreground mb-2">Official Contest Rules</h1>

      {/* Table of Contents */}
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <h2 className="text-sm font-display font-bold text-muted-foreground uppercase tracking-wider mb-3">Table of Contents</h2>
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {sections.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
              >
                {s.title}
              </a>
            ))}
          </nav>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardContent className="p-6 space-y-6 text-sm text-muted-foreground leading-relaxed">
          <section id="eligibility">
            <h2 className="text-base font-bold text-foreground mb-2">1. Eligibility</h2>
            <p>
              Participants must be at least 18 years of age and located in a jurisdiction where paid fantasy sports contests
              are legal. Employees of Sabotage Fantasy, their immediate family members, and persons residing in the same
              household are not eligible for paid contests.
            </p>
          </section>

          <section id="entry-fees">
            <h2 className="text-base font-bold text-foreground mb-2">2. Entry Fees & Prizes</h2>
            <p>
              Entry fees are set by the league commissioner at league creation and are non-refundable once the draft begins.
              Prize pools are distributed according to the league's pre-defined payout structure (visible in league settings).
              Sabotage Fantasy retains a platform fee of up to 10% of each entry fee.
            </p>
            <TimingCallout>Entry fees are non-refundable once the draft begins.</TimingCallout>
          </section>

          <section id="scoring">
            <h2 className="text-base font-bold text-foreground mb-2">3. Scoring System</h2>
            <p>
              Sabotage Fantasy uses an inverted scoring model. <strong className="text-foreground">Trash Points</strong> are
              earned from negative real-life statistics (interceptions, fumbles, missed kicks, penalties, etc.) and count
              positively toward your fantasy score. <strong className="text-foreground">Anti Points</strong> are deducted for
              positive real-life statistics (touchdowns, yards, completions, etc.). Your net <strong className="text-foreground">
              Sabotage Points</strong> = Trash Points - Anti Points. Higher is better.
            </p>
          </section>

          <section id="sabotage-cards">
            <h2 className="text-base font-bold text-foreground mb-2">4. Sabotage Cards</h2>
            <p>
              Each team receives a budget of sabotage credits at season start. Credits can be spent to play sabotage cards
              against opponents' rosters. Cards apply temporary effects (roster locks, point multipliers, position swaps, etc.)
              for one week. Cards cannot be played against your own team. All card plays are public and visible in the activity feed.
            </p>
          </section>

          <section id="disputes">
            <h2 className="text-base font-bold text-foreground mb-2">5. Disputes & Stat Corrections</h2>
            <p>
              Statistical corrections issued by the NFL's official stats provider within 72 hours of game completion will be
              applied automatically. The commissioner may manually trigger a rescore via Commissioner Tools. Disputes must be
              raised within 48 hours of the relevant game's completion. The commissioner's ruling is final for league-specific
              disputes; platform-level issues can be escalated to support@sabotagefantasy.com.
            </p>
            <TimingCallout>Stat corrections: applied within 72 hours. Disputes: must be raised within 48 hours.</TimingCallout>
          </section>

          <section id="trades-waivers">
            <h2 className="text-base font-bold text-foreground mb-2">6. Trades & Waivers</h2>
            <p>
              Trades require mutual acceptance by both parties. The commissioner may veto trades within 24 hours of acceptance
              if collusion is suspected. Waiver claims use a FAAB (Free Agent Acquisition Budget) blind-bid system, and
              <strong className="text-foreground"> when a claim is processed depends on why the player is on waivers</strong>:
              a player who was recently dropped clears overnight at 2:00 AM ET (1:00 AM ET after the November clock
              change, as that run is scheduled in UTC), while a player whose game has already been played this week
              clears on the league&apos;s weekly waiver day — Wednesday by default — at 2:00 AM ET year-round.
              Ties in bid amount are broken by inverse standings order.
            </p>
            {/* SAB-1058: the trade deadline, disclosed. Default shown because this page is
                league-agnostic; a commissioner may set 8-15 or remove it, and their own
                league's setting is shown on the Trades tab and in league settings. The
                sentence comes from the SAME helper the app renders, so the published terms
                and the in-app copy cannot drift apart. */}
            <p className="mt-2">{tradeDeadlineRule(parseTradeDeadline(undefined))} A commissioner may set the
              deadline anywhere from Week 8 to Week 15, or remove it; the league's own setting is shown on its Trades tab.</p>
            {/* SAB-1142c: this said "Waivers process daily at ...", and after played-this-week
                claims moved to a weekly run that was simply untrue — on a real-money product,
                in the document a manager cites when a claim outcome is disputed. Rules copy
                asserting a schedule the engine does not follow is a different category of
                wrong from a UI label, so it states BOTH classes rather than one time. */}
            <TimingCallout>
              Waivers clear overnight: recently dropped players nightly at 2:00 AM ET
              (1:00 AM after the November clock change), and players whose game has already
              been played on the league&apos;s weekly waiver day at 2:00 AM ET.
              Commissioner trade veto window: 24 hours.
            </TimingCallout>
          </section>

          <section id="roster-locks">
            <h2 className="text-base font-bold text-foreground mb-2">7. Roster Locks</h2>
            <p>
              Individual player roster slots lock at the scheduled kickoff time of that player's NFL game for the week.
              Locked players cannot be benched, dropped, or traded until the following week. Bye-week players remain unlocked
              but score zero points.
            </p>
            <TimingCallout>Roster slots lock at each player's individual game kickoff time.</TimingCallout>
          </section>

          <section id="tiebreakers">
            <h2 className="text-base font-bold text-foreground mb-2">8. Tiebreakers</h2>
            <p>
              Regular season matchup ties are resolved by: (1) higher Trash Points total, (2) bench scoring differential,
              (3) coin flip. Playoff ties use the same order.
            </p>
            <p className="mt-2">
              Regular-season standings determine playoff seeding. Teams are ranked by wins. If two or more teams are tied
              on wins, the tie is broken by <strong>total points scored</strong>, unless the commissioner selected a
              different tiebreaker before the draft.
            </p>
            <p className="mt-2">
              Commissioners may set the tiebreaker to either <strong>total points scored</strong> or{' '}
              <strong>head-to-head record</strong> among the tied teams. The tiebreaker is fixed when the draft completes
              and cannot be changed after that point. It is shown in your league's settings.
            </p>
            <p className="mt-2">
              Head-to-head record counts wins minus losses in games played between the tied teams only. Teams in this
              format may meet one, two or three times in a season, and all meetings count. If the tied teams have not
              played each other, or their head-to-head record is level, total points scored decides.
            </p>
            <p className="mt-2">
              If teams remain tied after every step, the tie is broken by a fixed, arbitrary ordering so that seeding is
              always determined.
            </p>
          </section>

          <section id="responsible-gaming">
            <h2 className="text-base font-bold text-foreground mb-2">9. Responsible Gaming</h2>
            <p>
              Sabotage Fantasy is committed to responsible gaming. You can set deposit limits, self-exclude, or close your
              account at any time via Account Settings. If you or someone you know has a gambling problem, call the
              National Council on Problem Gambling helpline: 1-800-522-4700. See our full{' '}
              <a href="/responsible-gaming" className="text-primary hover:underline">Responsible Gaming Policy</a>.
            </p>
          </section>

          <section id="governing-law">
            <h2 className="text-base font-bold text-foreground mb-2">10. Governing Law</h2>
            <p>
              These contest rules are governed by the laws of the State of Texas, without regard to conflict of law provisions.
              Any disputes arising under these rules shall be resolved exclusively in the state or federal courts located in
              Travis County, Texas.
            </p>
          </section>

          <section className="pt-4 border-t border-border">
            <p className="text-[11px] text-muted-foreground">
              Last updated: April 2026. Sabotage Fantasy reserves the right to modify these rules with 7 days notice to active
              participants. Continued participation after notice constitutes acceptance of modified rules.
            </p>
          </section>
        </CardContent>
      </Card>
    </MarketingLayout>
  );
}
